import { getService } from "./service";
import { handleError } from "./ErrorReducer";
import store from "../config/configureStore";
import { serviceNames } from "../utils/constants";

const options = [
	"modules",
	"applicants",
	"employees",
	"training",
	"codes",
	"security",
	"cad",
	"mobile",
	"settings",
	"rms",
	"analytics",
	"archive",
];

export const DICTIONARY_PERMISSIONS = "DICTIONARY_PERMISSIONS";
const PERMISSIONS = "PERMISSIONS";
const mergeAgencyPermissions =
	window.__RUNTIME_CONFIG__.REACT_APP_MERGE_AGENCY_PERMISSIONS === "true";

const defaultState = {
	loaded: false,
	settings: {},
	agencyList: [],
};
const getfalsePermissions = () => ({
	Create: false,
	Read: false,
	Edit: false,
	Delete: false,
	ViewDeleted: false,
});
const mergePermissions = (perm1, perm2) => {
	if (!perm1 && !perm2) return getfalsePermissions();
	if (!perm1) return perm2;
	if (!perm2) return perm1;

	return {
		Create: perm1.Create || perm2.Create,
		Read: perm1.Read || perm2.Read,
		Edit: perm1.Edit || perm2.Edit,
		Delete: perm1.Delete || perm2.Delete,
		ViewDeleted: perm1.ViewDeleted || perm2.ViewDeleted,
	};
};
export const getFullPermissions = (permGroup, permName, AgencyID = null) => {
	const state = store.store.getState();
	const permissions = state.permissions;
	return _getFullPermissions(permGroup, permName, AgencyID, permissions);
};

export const _getFullPermissions = (permGroup, permName, AgencyID, p) => {
	if (!p || !p.loaded) return getfalsePermissions();

	if (mergeAgencyPermissions || AgencyID === "any") {
		// merged full permissions
		let permissions = getfalsePermissions();

		if (p[permGroup]) {
			const perm2 = p[permGroup][permName];
			if (perm2) permissions = mergePermissions(permissions, perm2);
		}

		if (p.agencies) {
			const agencies = Object.keys(p.agencies);
			agencies.forEach((agencyId) => {
				if (p.agencies[agencyId] && p.agencies[agencyId][permGroup]) {
					const perm2 = p.agencies[agencyId][permGroup][permName];
					if (perm2)
						permissions = mergePermissions(permissions, perm2);
				}
			});
		}

		return permissions;
	}

	// single agency mode
	let permissions = getfalsePermissions();

	if (p[permGroup]) {
		const perm2 = p[permGroup][permName];
		if (perm2) permissions = mergePermissions(permissions, perm2);
	}

	if (
		AgencyID &&
		p.agencies &&
		p.agencies[AgencyID] &&
		p.agencies[AgencyID][permGroup]
	) {
		const perm2 = p.agencies[AgencyID][permGroup][permName];
		if (perm2) permissions = mergePermissions(permissions, perm2);
	}

	return permissions;
};

export const areAllPermissionsDisabled = (permissions) => {
	return Object.values(permissions).every((value) => value === false);
};

export const formatModulePermission = (moduleName, data, ignoreList = []) => {
	const result = {};

	for (const key in data) {
		if (ignoreList.includes(key)) continue;
		result[key] = data[key]?.Read === true;
	}

	return {
		[moduleName]: result,
	};
};

export const getModuleAccessPermission = (permGroup, AgencyID) => {
	const state = store.store.getState();
	const permissions = state.permissions;

	if (mergeAgencyPermissions || AgencyID === "any") {
		if (permissions[permGroup]) return permissions[permGroup];

		const agencies = Object.keys(permissions.agencies || {});
		if (!agencies.length) return null;

		for (let i = 0; i < agencies.length; ++i) {
			if (permissions.agencies[agencies[i]][permGroup]) {
				return permissions.agencies[agencies[i]][permGroup];
			}
		}
		return null;
	} else if (!AgencyID) {
		return permissions[permGroup] || null;
	} else {
		let access = permissions[permGroup];
		if (access) return access;
		if (permissions.agencies?.[AgencyID]?.[permGroup]) {
			return permissions.agencies[AgencyID][permGroup];
		}
		return null;
	}
};

export const getUserAccessPermission = (permGroup, permName, AgencyID = null) => {
	const state = store.store.getState();
	const permissions = state?.user?.userData?.user?.Permissions ? JSON.parse(state?.user?.userData?.user?.Permissions) : [];
	return _getUserAccessPermission(permGroup, permName, AgencyID, permissions);
};

const _getUserAccessPermission = (permGroup, permName, AgencyID, p) => {
	const permission = p?.find((permission) => permission?.ComponentType === permGroup && permission?.ComponentName === permName);

	if (permission){
		return Boolean(permission?.Permissions & 1)
	}
	return false;
};

export const getAccessPermission = (permGroup, permName, AgencyID = null) => {
	const state = store.store.getState();
	const permissions = state.permissions;
	return _getAccessPermission(permGroup, permName, AgencyID, permissions);
};

const _getAccessPermission = (permGroup, permName, AgencyID, p) => {
	if (!p.loaded || !p[permGroup]) return null; // return null if no data

	// If merged access permission or AgencyID is "any"
	if (mergeAgencyPermissions || AgencyID === "any") {
		// 1. Check generic access permission
		if (p[permGroup][permName]) return p[permGroup][permName];

		// 2. Check agency-specific permissions
		const agencies = Object.keys(p.agencies || {});
		if (!agencies.length) return null;

		for (let i = 0; i < agencies.length; ++i) {
			if (p.agencies[agencies[i]][permGroup]?.[permName]) {
				return p.agencies[agencies[i]][permGroup][permName];
			}
		}
		return null;
	}
	// No specific AgencyID
	else if (!AgencyID) {
		return p[permGroup][permName] || null;
	}
	// Specific AgencyID
	else {
		let access = p[permGroup][permName];
		if (access) return access;
		if (p.agencies?.[AgencyID]?.[permGroup]?.[permName]) {
			return p.agencies[AgencyID][permGroup][permName];
		}
		return null;
	}
};

export const getAgenciesAccessPermission = (permGroup, permName) => {
	const state = store.store.getState();
	const perms = state.permissions;
	if (_getAccessPermission(permGroup, permName, null, perms))
		return perms.agencyList;
	return perms.agencyList.filter((AgencyID) =>
		_getAccessPermission(permGroup, permName, AgencyID, perms)
	);
};

export const getGroups = () => {
	const state = store.store.getState();
	const p = state.permissions;
	if (!p.groups) return [];
	return p.groups.map((g) => g.Label);
};

export const getProfile = () => {
	const state = store.store.getState();
	const p = state.permissions;
	if (!p.profile) return "Unknown";
	return p.profile.Label;
};

let permissionsService;
export const subscribePermissions = () => {
	return async (dispatch) => {
		try {
			permissionsService = getService(serviceNames.permissionsAms);
			permissionsService.on("updated", () => {
				dispatch(getUserPermissions());
				//dispatch(getDictionaries());
			});
			permissionsService.on("error", () =>
				console.log("Error in permissions service listener")
			);
			permissionsService.on("unhandledRejection", (reason, p) => {
				console.log(
					"PermissionsReducer Unhandled Rejection at: Promise ",
					p,
					" reason: ",
					reason
				);
			});
		} catch (error) {
			dispatch(handleError(error));
		}
	};
};

export const unsubscribePermissions = () => (dispatch) => {
	if (permissionsService) {
		try {
			permissionsService.off("created");
			permissionsService.off("error");
			permissionsService.off("unhandledRejection");
			permissionsService = false;
		} catch (error) {
			console.log(
				"PermissionsReducer/unsubscribePermissions: error: ",
				error,
				error.code
			);
		}
	}
};

export const notifyUpdatedPermissions = () => {
	const service = getService(serviceNames.permissionsAms);
	return service.get(0);
};

export const getUserPermissions = () => async (dispatch) => {
	try {
		const service = getService(serviceNames.permissionsAms);
		const permissions = await service.find({
			query: { options },
		});
		await dispatch({
			type: PERMISSIONS,
			payload: { ...permissions, loaded: true },
		});
	} catch (err) {
		dispatch(handleError(err));
	}
};

export const getSpecificUserPermissions = async (ptsProfileID) => {
	try {
		const service = getService(serviceNames.permissionsAms);
		return await service.get(ptsProfileID);
	} catch (err) {
		return null;
	}
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function (state = defaultState, action) {
	switch (action.type) {
		case PERMISSIONS:
			return { ...state, ...action.payload };
		default:
			return state;
	}
}
